import { useGeneralStore, type CustomerBootstrapStatus } from '@/store/useGeneralStore'

/**
 * Where the customer's company stands, as far as a page is concerned:
 *
 * - `ready`: the account has a company (an id). Its type can still be null on
 *   legacy rows or after an admin edit; pages render what they can without it.
 * - `missing`: the bootstrap answered and this account has no company.
 * - `failed`: the bootstrap read failed and no company is on hand.
 * - `loading`: the bootstrap has not answered yet.
 *
 * `missing` is not an error: the customer has a step left. Only `failed` is.
 */
export type CompanyPresence = 'loading' | 'failed' | 'missing' | 'ready'

interface CompanyShape {
  company_id?: string | null
}

export function resolveCompanyPresence(
  company: CompanyShape | null | undefined,
  bootstrapStatus: CustomerBootstrapStatus | undefined,
): CompanyPresence {
  if (company?.company_id) return 'ready'
  if (bootstrapStatus === 'failed') return 'failed'
  if (bootstrapStatus === 'loaded') return 'missing'
  return 'loading'
}

export function useCompanyPresence(): CompanyPresence {
  const company = useGeneralStore((state) => state.companyDetail)
  const bootstrapStatus = useGeneralStore((state) => state.customerBootstrapStatus)
  return resolveCompanyPresence(company, bootstrapStatus)
}
